import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Shield,
  TrendingUp,
  TrendingDown,
  Activity,
  RefreshCw,
  BarChart3,
  PieChart,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./cards";
import {
  EndUserRiskDashboardApi,
  RiskDashboardApi,
} from "../../../services/axios/AuthApis/RisksManagementApis";
import { useSelector } from "react-redux";
import ApiLoader from "../../ui/ApiLoader";
import NotificationCenter from "../../ui/NotificationCenter";

export const RiskDashboard = ({ userId, userRole }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const selectedCompany = useSelector((state) => state.company.selectedCompany);

  const fetchStats = async () => {
    try {
      setLoading(true);
      let res;
      if (
        (userRole == "security_analyst" || userRole == "auditor") &&
        selectedCompany
      ) {
        res = await RiskDashboardApi(selectedCompany.user_id);
      } else res = await RiskDashboardApi(userId);
      if (res.success) {
        setStats(res.data);
      }
    } catch (err) {
      console.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return <ApiLoader label="Loading Dashboard…" className="py-20" />;
  }

  const getRatingClass = (rating) => {
    if (rating?.includes("Critical")) return "risk-critical";
    if (rating?.includes("High")) return "risk-high";
    if (rating?.includes("Significant")) return "risk-significant";
    if (rating?.includes("Moderate")) return "risk-moderate";
    return "risk-low";
  };

  // Calculate 5x5 heatmap from stats
  const heatmapData = stats?.heatmap_5x5 || {};

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Metrics Grid + Notifications */}
      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <div className="w-full lg:w-[28rem] flex-shrink-0 h-[380px]">
          <NotificationCenter module="risk_management" title="Risk Management Notifications" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
          <MetricCard
            icon={BarChart3}
            title="Total Risks"
            value={stats?.total_risks || 0}
            color="blue"
            testId="metric-total-risks"
            onClick={() => navigate("/risk-management?tab=register")}
          />
          <MetricCard
            title="Critical/High Risks"
            value={stats?.critical_high_risks || 0}
            icon={AlertTriangle}
            color="red"
            testId="metric-critical-risks"
            onClick={() => navigate("/risk-management?tab=register")}
          />
          <MetricCard
            title="Moderate Risks"
            value={stats?.moderate_risks || 0}
            icon={Activity}
            color="amber"
            testId="metric-moderate-risks"
            onClick={() => navigate("/risk-management?tab=register")}
          />
          <MetricCard
            title="Low Risks"
            value={stats?.low_risks || 0}
            icon={Shield}
            color="green"
            testId="metric-low-risks"
            onClick={() => navigate("/risk-management?tab=register")}
          />
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Risk Heatmap - 5x5 Matrix */}
        <Card className="lg:col-span-2 card-surface">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              Risk Assessment Matrix (5x5)
            </CardTitle>
            <p className="text-xs text-slate-500">
              Inherent Risk: Impact vs Likelihood
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-6 gap-1" data-testid="risk-heatmap">
              {/* Header Row */}
              <div className="text-xs text-slate-500 font-medium p-2"></div>
              <div className="text-xs text-slate-500 font-medium p-2 text-center">
                Rare (1)
              </div>
              <div className="text-xs text-slate-500 font-medium p-2 text-center">
                Unlikely (2)
              </div>
              <div className="text-xs text-slate-500 font-medium p-2 text-center">
                Possible (3)
              </div>
              <div className="text-xs text-slate-500 font-medium p-2 text-center">
                Likely (4)
              </div>
              <div className="text-xs text-slate-500 font-medium p-2 text-center">
                Almost Certain (5)
              </div>

              {/* Data Rows - Impact levels from Catastrophic to Insignificant */}
              {[
                { label: "Catastrophic (5)", impact: 5 },
                { label: "Major (4)", impact: 4 },
                { label: "Moderate (3)", impact: 3 },
                { label: "Minor (2)", impact: 2 },
                { label: "Insignificant (1)", impact: 1 },
              ].map((row) => (
                <React.Fragment key={row.impact}>
                  <div className="text-xs text-slate-500 font-medium p-2 flex items-center">
                    {row.label}
                  </div>
                  {[1, 2, 3, 4, 5].map((likelihood) => {
                    const score = row.impact + likelihood;
                    const count =
                      heatmapData[`${row.impact}-${likelihood}`] || 0;
                    let cellClass = "bg-green-100 text-green-700";
                    if (score >= 9) cellClass = "bg-red-500 text-white";
                    else if (score >= 7) cellClass = "bg-orange-400 text-white";
                    else if (score >= 5) cellClass = "bg-amber-400 text-white";
                    else if (score >= 3)
                      cellClass = "bg-yellow-300 text-yellow-800";

                    return (
                      <div
                        key={`${row.impact}-${likelihood}`}
                        className={`h-14 rounded flex items-center justify-center text-sm font-semibold ${cellClass}`}
                        data-testid={`heatmap-cell-${row.impact}-${likelihood}`}
                      >
                        {count}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100">
              <span className="text-xs text-slate-500">Risk Level:</span>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-red-500"></span>
                <span className="text-xs text-slate-600">Critical</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-orange-400"></span>
                <span className="text-xs text-slate-600">High</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-amber-400"></span>
                <span className="text-xs text-slate-600">Significant</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-yellow-300"></span>
                <span className="text-xs text-slate-600">Moderate</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-green-100"></span>
                <span className="text-xs text-slate-600">Low</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Risk Distribution */}
        <Card className="card-surface">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-500" />
              Risk Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(stats?.categories || {}).map(
              ([category, count]) => (
                <div key={category} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 truncate">{category}</span>
                    <span className="font-semibold text-slate-800">
                      {count}
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${stats?.total_risks ? (count / stats.total_risks) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </div>
              ),
            )}
          </CardContent>
        </Card>
      </div>

      {/* Risk by Sub Category */}
      <Card className="card-surface">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-800 dark:text-white">
            Risk by Sub Category
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(stats?.sub_categories || {})
              .slice(0, 12)
              .map(([subCat, count]) => (
                <div
                  key={subCat}
                  className="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 text-center"
                  data-testid={`subcategory-${subCat.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p className="text-2xl font-bold text-pink-600 dark:text-pink-400">
                    {count}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate">
                    {subCat}
                  </p>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const MetricCard = ({ title, value, icon: Icon, color, testId, onClick }) => {
  const colorClasses = {
    pink: "text-pink-600 bg-pink-50 dark:text-pink-300 dark:bg-pink-900/20",
    red: "text-red-600 bg-red-50 dark:text-red-300 dark:bg-red-900/20",
    amber:
      "text-amber-600 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/20",
    green:
      "text-green-600 bg-green-50 dark:text-green-300 dark:bg-green-900/20",
    blue: "text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/20",
  };

  return (
    <div
      className={`metric-card bg-white ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
      data-testid={testId}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="flex items-start gap-3">
        <div
          className={`p-2 rounded-lg ${colorClasses[color]} flex items-center justify-center`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
            {title}
          </p>
          <p className="text-3xl font-bold text-slate-800 dark:text-white mt-1">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
};
